/* iter-118 — Pacer in-module search.
 *
 * Surfaces inside <PacerRoadmap />. A pill-shaped search input that
 * opens a results panel filtering Pacer tasks by:
 *   • Task name / description
 *   • Assignee (responsible)
 *   • Project
 *   • Department
 *
 * Pure client-side filtering over the already-loaded `tasks` array
 * (1,000+ rows). Substring matching, accent-insensitive, case-insensitive.
 * Optional facet pills scope the search to one specific field.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Search, X, ListChecks, User, Briefcase, Building2, Bookmark, Star, Trash2, Plus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FACETS = [
  { id: 'all',        label: 'All',        Icon: Search },
  { id: 'task',       label: 'Task',       Icon: ListChecks },
  { id: 'user',       label: 'User',       Icon: User },
  { id: 'project',    label: 'Project',    Icon: Briefcase },
  { id: 'department', label: 'Department', Icon: Building2 },
];

const ADMIN_LIKE_ROLES = ['admin', 'engineer', 'ownership', 'top_leader'];

function normalize(s) {
  return (s || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function fieldFor(t, facet) {
  switch (facet) {
    case 'task':       return [t.task_name, t.additional_info];
    case 'user':       return [t.responsible];
    case 'project':    return [t.project];
    case 'department': return [t.department, t.sub_section];
    default:           return [t.task_name, t.additional_info, t.responsible, t.project, t.department, t.sub_section];
  }
}

export default function PacerSearch({ tasks, departments, onPickTask }) {
  const [q, setQ] = useState('');
  const [facet, setFacet] = useState('all');
  const [savedFilters, setSavedFilters] = useState([]);
  const [showSavedMenu, setShowSavedMenu] = useState(false);
  const [showSavePopover, setShowSavePopover] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveScope, setSaveScope] = useState('user');
  const [saving, setSaving] = useState(false);

  const token = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
  const userRole = typeof window !== 'undefined' ? (localStorage.getItem('user_role') || 'viewer').toLowerCase() : 'viewer';
  const canCreateTeam = ADMIN_LIKE_ROLES.includes(userRole);

  // Load saved filters (signed-in users only).
  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/pacer/saved-filters`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setSavedFilters(data?.filters || []))
      .catch(() => {});
  }, [token]);

  const applyFilter = (f) => {
    setQ(f.q || '');
    setFacet(f.facet || 'all');
    setShowSavedMenu(false);
  };

  const saveCurrent = async () => {
    if (!saveName.trim()) return;
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/pacer/saved-filters`,
        { name: saveName.trim(), q, facet, scope: saveScope },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSavedFilters((prev) => [...prev, data].sort((a, b) => (a.scope !== 'team') - (b.scope !== 'team') || a.name.localeCompare(b.name)));
      setShowSavePopover(false);
      setSaveName('');
      setSaveScope('user');
    } catch {
      // toast not imported here; silently fail
    } finally {
      setSaving(false);
    }
  };

  const deleteFilter = async (id) => {
    try {
      await axios.delete(`${API}/pacer/saved-filters/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSavedFilters((prev) => prev.filter((f) => f.id !== id));
    } catch { /* ignore */ }
  };

  const matched = useMemo(() => {
    const needle = normalize(q.trim());
    if (!needle) return [];
    return tasks.filter((t) => fieldFor(t, facet).some((v) => normalize(v).includes(needle))).slice(0, 50);
  }, [q, facet, tasks]);

  const accentOf = (dept) => {
    const d = departments.find((x) => x.department === dept);
    return (d && d.accent_color) || '#1F4FBE';
  };

  return (
    <div data-testid="pacer-search" className="mb-6">
      {/* Search input + facet pills */}
      <div className="rounded-2xl border bg-white shadow-sm" style={{ borderColor: '#E5E7EB' }}>
        <div className="flex items-center gap-2 px-4 py-2.5">
          <Search className="w-4 h-4 flex-shrink-0" style={{ color: '#1F4FBE' }} />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search Pacer · task, user, project, department…"
            className="flex-1 bg-transparent outline-none text-[13px]"
            style={{ fontFamily: 'Manrope', color: '#1A2E2E' }}
            data-testid="pacer-search-input"
          />
          {/* Saved Filters dropdown (signed-in only) */}
          {token && (
            <div className="relative">
              <button
                type="button"
                onClick={() => { setShowSavedMenu((s) => !s); setShowSavePopover(false); }}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-bold transition-colors"
                style={{ background: '#F1F5F9', color: '#475569', fontFamily: 'Manrope' }}
                data-testid="pacer-saved-filters-btn"
                title="Saved filters"
              >
                <Bookmark className="w-3 h-3" />
                Saved
                {savedFilters.length > 0 && (
                  <span className="ml-1 px-1 rounded-full text-[9px]" style={{ background: '#1F4FBE', color: 'white' }}>
                    {savedFilters.length}
                  </span>
                )}
              </button>
              {showSavedMenu && (
                <div
                  className="absolute right-0 top-full mt-1 w-72 max-h-80 overflow-y-auto rounded-lg border bg-white shadow-lg z-20"
                  style={{ borderColor: '#E5E7EB' }}
                  data-testid="pacer-saved-filters-menu"
                >
                  {savedFilters.length === 0 ? (
                    <p className="px-3 py-3 text-[11px] italic text-center" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
                      No saved filters yet. Save your current search to pin it.
                    </p>
                  ) : (
                    <ul className="divide-y" style={{ borderColor: '#F3F4F6' }}>
                      {savedFilters.map((f) => (
                        <li key={f.id} className="flex items-center gap-2 px-3 py-2 hover:bg-blue-50/40 group">
                          <button
                            type="button"
                            onClick={() => applyFilter(f)}
                            className="flex-1 text-left flex items-center gap-2 min-w-0"
                            data-testid={`pacer-saved-filter-${f.id}`}
                          >
                            {f.scope === 'team' ? (
                              <Star className="w-3 h-3 flex-shrink-0" style={{ color: '#D4AF37' }} />
                            ) : (
                              <Bookmark className="w-3 h-3 flex-shrink-0" style={{ color: '#475569' }} />
                            )}
                            <div className="min-w-0">
                              <div className="text-[12px] font-bold truncate" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>
                                {f.name}
                              </div>
                              <div className="text-[9.5px] truncate" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
                                {f.scope === 'team' ? 'Team · ' : ''}{f.facet} · &ldquo;{f.q || '—'}&rdquo;
                              </div>
                            </div>
                          </button>
                          {(f.scope === 'user' || canCreateTeam) && (
                            <button
                              type="button"
                              onClick={() => deleteFilter(f.id)}
                              className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-red-50 text-red-600"
                              aria-label="Delete saved filter"
                              data-testid={`pacer-saved-filter-delete-${f.id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}
          {/* Save current */}
          {token && q.trim() && (
            <div className="relative">
              <button
                type="button"
                onClick={() => { setShowSavePopover((s) => !s); setShowSavedMenu(false); setSaveName(q.slice(0, 40)); }}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-bold transition-colors"
                style={{ background: '#1F4FBE', color: 'white', fontFamily: 'Manrope' }}
                data-testid="pacer-save-filter-btn"
              >
                <Plus className="w-3 h-3" />
                Save
              </button>
              {showSavePopover && (
                <div
                  className="absolute right-0 top-full mt-1 w-72 p-3 rounded-lg border bg-white shadow-lg z-20"
                  style={{ borderColor: '#E5E7EB' }}
                  data-testid="pacer-save-filter-popover"
                >
                  <label className="text-[10px] uppercase tracking-wider font-bold" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>Name</label>
                  <input
                    type="text"
                    value={saveName}
                    onChange={(e) => setSaveName(e.target.value)}
                    placeholder="e.g. My open tasks · Engineering"
                    className="w-full mt-1 px-2 py-1.5 rounded border text-[12px] outline-none"
                    style={{ borderColor: '#E5E7EB', fontFamily: 'Manrope' }}
                    data-testid="pacer-save-filter-name"
                    autoFocus
                  />
                  <label className="text-[10px] uppercase tracking-wider font-bold mt-2 block" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>Scope</label>
                  <div className="mt-1 flex gap-1.5">
                    <button
                      type="button"
                      onClick={() => setSaveScope('user')}
                      className="flex-1 px-2 py-1 rounded text-[11px] font-bold"
                      style={{ background: saveScope === 'user' ? '#1F4FBE' : '#F1F5F9', color: saveScope === 'user' ? 'white' : '#475569', fontFamily: 'Manrope' }}
                      data-testid="pacer-save-filter-scope-user"
                    >
                      My filter
                    </button>
                    <button
                      type="button"
                      onClick={() => canCreateTeam && setSaveScope('team')}
                      disabled={!canCreateTeam}
                      className="flex-1 px-2 py-1 rounded text-[11px] font-bold inline-flex items-center justify-center gap-1"
                      style={{
                        background: saveScope === 'team' ? '#D4AF37' : '#F1F5F9',
                        color: saveScope === 'team' ? 'white' : (canCreateTeam ? '#475569' : '#94A3B8'),
                        fontFamily: 'Manrope',
                        cursor: canCreateTeam ? 'pointer' : 'not-allowed',
                      }}
                      data-testid="pacer-save-filter-scope-team"
                      title={canCreateTeam ? 'Visible to all signed-in users' : 'Admins/Engineers only'}
                    >
                      <Star className="w-3 h-3" /> Team
                    </button>
                  </div>
                  <div className="mt-3 flex gap-1.5">
                    <button
                      type="button"
                      onClick={saveCurrent}
                      disabled={saving || !saveName.trim()}
                      className="flex-1 px-2 py-1.5 rounded text-[11px] font-bold text-white disabled:opacity-50"
                      style={{ background: '#1F4FBE', fontFamily: 'Manrope' }}
                      data-testid="pacer-save-filter-confirm"
                    >
                      {saving ? 'Saving…' : 'Save filter'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowSavePopover(false)}
                      className="px-2 py-1.5 rounded text-[11px] font-bold"
                      style={{ background: '#F1F5F9', color: '#475569', fontFamily: 'Manrope' }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          {q && (
            <button
              type="button"
              onClick={() => setQ('')}
              className="p-1 rounded hover:bg-gray-100 text-gray-500"
              aria-label="Clear search"
              data-testid="pacer-search-clear"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1.5 px-3 pb-2 border-t pt-2" style={{ borderColor: '#F3F4F6' }}>
          {FACETS.map(({ id, label, Icon }) => {
            const active = facet === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setFacet(id)}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wide transition-colors"
                style={{
                  background: active ? '#1F4FBE' : '#F1F5F9',
                  color: active ? 'white' : '#475569',
                  fontFamily: 'Manrope',
                }}
                data-testid={`pacer-search-facet-${id}`}
              >
                <Icon className="w-3 h-3" />
                {label}
              </button>
            );
          })}
          {q && (
            <span className="ml-auto text-[10px] font-bold" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
              {matched.length} result{matched.length === 1 ? '' : 's'}
            </span>
          )}
        </div>
      </div>

      {/* Results panel */}
      {q && (
        <div
          className="mt-2 rounded-2xl border bg-white shadow-sm max-h-[420px] overflow-y-auto"
          style={{ borderColor: '#E5E7EB' }}
          data-testid="pacer-search-results"
        >
          {matched.length === 0 ? (
            <p className="px-4 py-6 text-center text-[12px] italic" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
              No tasks match &ldquo;{q}&rdquo;.
            </p>
          ) : (
            <ul className="divide-y" style={{ borderColor: '#F3F4F6' }}>
              {matched.map((t) => {
                const accent = accentOf(t.department);
                return (
                  <li key={t.id}>
                    <button
                      type="button"
                      onClick={() => onPickTask?.(t, accent)}
                      className="w-full text-left px-3 py-2 hover:bg-blue-50/40 flex items-start gap-3"
                      data-testid={`pacer-search-result-${t.id}`}
                    >
                      <span
                        className="mt-0.5 inline-block w-1.5 h-6 rounded-full flex-shrink-0"
                        style={{ background: accent }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-[12.5px] font-bold leading-snug line-clamp-2" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>
                          {t.task_name}
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px]" style={{ color: '#6B7280', fontFamily: 'Manrope' }}>
                          <span className="inline-flex items-center gap-1"><Building2 className="w-2.5 h-2.5" />{t.department || '—'}</span>
                          {t.project && <span className="inline-flex items-center gap-1"><Briefcase className="w-2.5 h-2.5" />{t.project}</span>}
                          {t.responsible && <span className="inline-flex items-center gap-1"><User className="w-2.5 h-2.5" />{t.responsible.split(/[·,]/)[0].trim()}</span>}
                          {t.target_date_raw && <span>· {t.target_date_raw}</span>}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
